import { Box, Stack, Tooltip, Typography } from '@mui/material';
import AuthorList from '../Article/AuthorList';
import CategoryList from './CategoryList';
import VideoChip from './VideoChip';
import dayjs from 'dayjs';
import ArticleMeta from '../../types/ArticleMeta';
import { ArticleCardVariant } from './CardUtils';
import { useTranslation } from 'react-i18next';
import ComingSoonChip from '../Article/ComingSoonChip';

interface ArticleCardFooterProps {
  readonly meta: ArticleMeta;
  readonly variant: ArticleCardVariant;
  readonly hidePrimaryCategory?: boolean;
  readonly hideCategories?: boolean;
  readonly onAuthorClick?: () => void;
  readonly onCategoryClick?: () => void;
}

export default function ArticleCardFooter({
  meta,
  variant,
  hidePrimaryCategory = false,
  hideCategories = false,
  onAuthorClick = () => {},
  onCategoryClick = () => {},
}: ArticleCardFooterProps) {
  const { t } = useTranslation();

  return (
    <Box>
      <Stack
        direction='row'
        useFlexGap
        sx={{
          width: '100%',
          alignItems: 'center',
          flexWrap: 'wrap',
          columnGap: 2,
        }}
      >
        {meta.authors.length > 0 && (
          <AuthorList
            variant='subtitle2'
            authors={meta.authors}
            StackProps={{ marginY: 1.25 }}
            TypographyProps={{ paddingY: 0.25 }}
            onClick={onAuthorClick}
          />
        )}
        {!meta.isUpcoming && (
          <Tooltip
            title={t('content.releasedOnDate', {
              date: dayjs(meta.releaseDate).format('D.M.YYYY'),
            })}
            placement='top'
          >
            <Typography
              variant='body2'
              sx={{
                color: 'text.secondary',
                marginY: meta.authors.length === 0 ? 1.5 : 0,
              }}
            >
              {dayjs(meta.releaseDate).format('D.M.YYYY')}
            </Typography>
          </Tooltip>
        )}
        {meta.isUpcoming && <ComingSoonChip />}
        {variant === 'default' && meta.hasVideoContent() && <VideoChip />}
      </Stack>
      {variant === 'mobile' && meta.hasVideoContent() && (
        <Stack
          direction='row'
          sx={{
            marginBottom: 1,
          }}
        >
          <VideoChip />
        </Stack>
      )}
      {!hideCategories && (
        <CategoryList
          variant={variant === 'default' ? 'default' : 'mobile'}
          categories={
            hidePrimaryCategory
              ? meta.categories.secondary
              : meta.allCategories()
          }
          onClick={onCategoryClick}
        />
      )}
    </Box>
  );
}
